import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sun, Moon, ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Register = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Fetch tenant ID if artistSlug is provided
  React.useEffect(() => {
    if (artistSlug) {
      axios.get(`${API_URL}/artists/public/${artistSlug}`)
        .then(res => setTenantId(res.data.artist?.id))
        .catch(() => {});
    }
  }, [artistSlug]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        role: artistSlug ? 'FAN' : 'FAN', // Default to FAN for public registration
        tenantId: tenantId,
      };
      
      const result = await register(userData);
      
      // Si validation email requise, afficher le message de succès
      if (result.requiresVerification) {
        setRegistrationSuccess(true);
        setRegisteredEmail(formData.email);
        toast.success('Compte créé ! Vérifiez votre email pour activer votre compte.');
        return;
      }
      
      // Sinon, rediriger directement
      toast.success('Compte créé avec succès!');
      
      if (artistSlug) {
        navigate(`/${artistSlug}/fan/account`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 hero-gradient">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(/images/concert-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50"
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
      
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(artistSlug ? `/${artistSlug}` : '/')}
        className="fixed top-4 left-4 z-50"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('back')}
      </Button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="glass-card">
          <CardHeader className="text-center space-y-2">
            <Link to="/" className="flex items-center justify-center gap-3 mb-4">
              <SenArtisteLogo className="w-16 h-16" />
              <span className="font-syne font-bold text-2xl">Sen Artiste</span>
            </Link>
            <div className="flex justify-center">
              <LanguageSelector />
            </div>
            <CardTitle className="font-syne text-2xl">{t('register')}</CardTitle>
            <CardDescription>
              {artistSlug 
                ? (t('language') === 'fr' ? 'Rejoignez la communauté des fans' : 'Join the fan community')
                : (t('language') === 'fr' ? 'Commencez votre aventure' : 'Start your journey')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Message de succès avec validation email */}
            {registrationSuccess ? (
              <div className="text-center py-6" data-testid="registration-success">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Vérifiez votre email</h3>
                <p className="text-muted-foreground mb-4">
                  Un email de validation a été envoyé à<br />
                  <span className="font-medium text-foreground">{registeredEmail}</span>
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Cliquez sur le lien dans l'email pour activer votre compte.<br />
                  N'oubliez pas de vérifier vos spams.
                </p>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="w-full"
                    data-testid="go-to-login"
                  >
                    Aller à la connexion
                  </Button>
                  <Link 
                    to="/verify-email" 
                    className="text-sm text-muted-foreground hover:text-primary underline"
                  >
                    Renvoyer l'email de validation
                  </Link>
                </div>
              </div>
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t('firstName')}</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder={t('firstName')}
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    data-testid="register-firstName"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t('lastName')}</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder={t('lastName')}
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    data-testid="register-lastName"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">{t('phone')} ({t('language') === 'fr' ? 'optionnel' : 'optional'})</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+221 77 000 00 00"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="register-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="register-password"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirmPassword"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  t('signUp')
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('hasAccount')}{' '}
                <Link 
                  to={artistSlug ? `/${artistSlug}/login` : '/login'} 
                  className="text-primary hover:underline"
                  data-testid="login-link"
                >
                  {t('signIn')}
                </Link>
              </p>
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Register;
